import {environment} from "environments";

class BatchingRulesTable {
  constructor(Batching, toastr, $timeout, $scope) {
    Object.assign(this, {
      Batching,
      toastr,
      $timeout,
      $scope,
      rules: [],
      combinedRulesList: [],
      filteredCombinedRulesList: [],
      rulesInView: [],
      buffer: 20,
      searchQuery: '',
      startIndex: 0,
      observer: null,
      intersectionCount: 5,
      lastObservedElement: null,
      BOD_2146: ['localhost', 'dev', 'qa', 'staging', 'production'].includes(environment.constants.ENV),
    });
  }

  $onInit() {
    this.Batching.getBatchRules()
      .then((rules) => {
        this.rules = rules.map(rule => ({
          ...rule,
          editing: false,
          editable: {
            is_active: rule.is_active,
            params: rule.params,
          },
        }));
        this.combinedRulesList = this.rules;
        /*
         BOD_2146 entry point:
         this entry point handles the creation of the observer,
         also offloads the old filter pipe to be handled in JS so we
         can take control of the actual filter on the rules array,
         and update the view itself.
         The other functions listed below are new and should not be called
         anywhere else unless from a new function itself.
         */
        if(this.BOD_2146) {
          this.filterRules();
          this.updateRulesInView();
          this.setupIntersectionObserver();
        }
      });

      this.$scope.$on('saveBatchingRule', (event,rule) => {
        this.saveRule(rule);
      });

      this.$scope.$on('deleteBatchingRule', (event,rule) => {
        this.deleteRule(rule);
      });
  }

  $onChanges(changesObj) {
    if (changesObj.additionalRules) {
      const { currentValue } = changesObj.additionalRules;
      currentValue.forEach((newRule) => {
        newRule.editing = false;
        newRule.editable = {
          is_active: true,
          params: newRule.params,
        };
      });
      this.combinedRulesList = [
        ...currentValue,
        ...this.combinedRulesList,
      ];
    }

    if (changesObj.data) {
      const prev = changesObj.data.previousValue || [];
      const curr = this.data || [];

      // If there are selected IDs, filter the rules in view and pause infinite scroll
      if (curr.length > 0) {
        const dataSet = new Set(curr);
        this.rulesInView = this.combinedRulesList.filter(item => dataSet.has(item.id));
        if (this.observer && this.lastObservedElement) {
          this.observer.unobserve(this.lastObservedElement);
        }
      }

      // If the filter is cleared (transition from non-empty to empty), reset the view and resume infinite scroll
      if (prev.length > 0 && curr.length === 0) {
        this.filterRules();
        this.updateRulesInView();
        this.setupIntersectionObserver();
      }
    }

  };

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1,
    };
    const callback = (entries, observer) => {
      if(entries[0].isIntersecting) {
        this.buffer += (Math.min(10, this.filteredCombinedRulesList.length - this.buffer));
        this.updateRulesInView();
        this.$timeout(() => {
          const newLastElement = document.querySelectorAll('.batch_rule-row')[this.buffer - this.intersectionCount];
          if (this.lastObservedElement) {
            observer.unobserve(this.lastObservedElement);
          }
          observer.observe(newLastElement);
          this.lastObservedElement = newLastElement;
        }, 0);
      }
    };

    this.observer = new IntersectionObserver(callback, options);
    this.$timeout(() => {
      this.lastObservedElement = document.querySelectorAll('.batch_rule-row')[this.buffer - this.intersectionCount];
      this.observer.observe(this.lastObservedElement);
    }, 0);
  }

  updateRulesInView() {
    this.rulesInView = this.filteredCombinedRulesList.slice(this.startIndex, this.startIndex + this.buffer);
  }

  filterRules() {
    if (this.searchQuery.length > 2) {
      const query = this.searchQuery ? this.searchQuery.toLowerCase() : '';
      this.filteredCombinedRulesList = this.combinedRulesList.filter(rule => {
        return (rule.entity && rule.entity.toLowerCase().includes(query)) ||
          (rule.entity_type && rule.entity_type.toLowerCase().includes(query)) ||
          (rule.rule && rule.rule.toLowerCase().includes(query)) ||
          (rule.params && rule.params.toLowerCase().includes(query)) ||
          (rule.service && rule.service.toLowerCase().includes(query)) ||
          (rule.product && rule.product.toLowerCase().includes(query));
      });
    } else {
      this.filteredCombinedRulesList = this.combinedRulesList;
    }
    this.updateRulesInView();
  }

  cancelEdit(rule) {
    rule.editable = {
      is_active: rule.is_active,
      params: rule.params,
    };
    rule.editing = false;
  }

  editRule(rule) {
    rule.editing = true;
    this.parent.openBatchingRuleDialog(rule);
  }

  saveRule(rule) {
    this.Batching.updateBatchRule(rule)
      .then(() => {
        Object.keys(rule.editable).forEach((key) => {
          rule[key] = rule.editable[key];
        });
        rule.editing = false;
        // Close the modal if open
        if (this.parent && typeof this.parent.ngDialog !== 'undefined') {
          this.parent.ngDialog.close();
        }
      })
      .catch((err) => {
        this.toastr.error("Updating Rule failed - try again and double check all params: \n" + err);
      });
  }
  deleteRule(rule) {
    this.Batching.deleteBatchRule(rule)
      .then(() => {
        this.combinedRulesList = this.combinedRulesList
          .filter(thisRule => thisRule.id !== rule.id);
        rule.editing = false;
        // Close the modal if open
        if (this.parent && typeof this.parent.ngDialog !== 'undefined') {
          this.parent.ngDialog.close();
        }
      })
      .catch((err) => {
        this.toastr.error("Deleting Rule failed - try again: \n" + err);
      });
  }
  copyRule(rule) {
    this.onCopy({ rule });
  }
}

BatchingRulesTable.$inject = ['Batching', 'toastr', '$timeout', '$scope'];
angular.module("pims").component('batchingRulesTable', {
  templateUrl: 'app/views/settings/batching-rules/batching-rules-table/batching-rules-table.component.html',
  controller: BatchingRulesTable,
  controllerAs: 'vm',
  bindings: {
    additionalRules: '<',
    onCopy: '&',
    data: '<',
    parent: '=',
  },
});
